// src/components/NotificationItem.tsx

import React from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Bell, Calendar, Carrot, Building2, Info, AlertTriangle } from 'lucide-react';
import type { NotificationType } from '../../types/notifications';

const formatDateTime = (date: string | Date | { seconds: number; nanoseconds: number }) => {
  let dateObj: Date;

  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'string') {
    dateObj = new Date(date);
  } else if (date && 'seconds' in date) {
    // Handle Firestore Timestamp
    dateObj = new Date(date.seconds * 1000);
  } else {
    // Fallback für ungültige Daten
    return { date: 'Unbekannt', time: 'Unbekannt' };
  }

  try {
    return {
      date: format(dateObj, 'dd. MMMM yyyy', { locale: de }),
      time: format(dateObj, 'HH:mm', { locale: de })
    };
  } catch (error) {
    console.error('Error formatting date:', error);
    return { date: 'Unbekannt', time: 'Unbekannt' };
  }
};

interface NotificationItemProps {
  id: string;
  metadata: NotificationType;
  createdAt: Date;
  readAt?: Date | null;
  priority: string;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void; // Neue prop für die Löschfunktion
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  id,
  metadata,
  createdAt,
  readAt,
  priority,
  onMarkAsRead,
  onDelete
}) => {
  const getNotificationContent = (metadata: NotificationType) => {
    switch (metadata.type) {
      // Horse notifications
      case 'HORSE_CREATED':
        return {
          icon: Carrot,
          title: 'Neues Pferd registriert',
          message: `Ein neues Pferd "${metadata.horseName}" wurde in Ihrem Stall registriert.`
        };
      case 'HORSE_UPDATE':
        return {
          icon: Carrot,
          title: 'Pferdedaten aktualisiert',
          message: `Die Daten von "${metadata.horseName}" wurden aktualisiert.`
        };
      case 'HORSE_DELETED':
        return {
          icon: AlertTriangle,
          title: 'Pferd entfernt',
          message: `Das Pferd "${metadata.horseName}" wurde aus dem System entfernt.`
        };
      case 'HORSE_CORIDER_REQUEST':
        return {
          icon: Carrot,
          title: 'Neue Reitbeteiligungsanfrage',
          message: `${metadata.details?.requesterName} möchte Reitbeteiligung für "${metadata.horseName}" werden.`
        };
      case 'HORSE_CORIDER_UPDATE':
        return {
          icon: Carrot,
          title: 'Status der Reitbeteiligung aktualisiert',
          message: metadata.details?.status === 'approved'
            ? `Ihre Anfrage für eine Reitbeteiligung bei "${metadata.horseName}" wurde angenommen.`
            : `Ihre Reitbeteiligung für "${metadata.horseName}" wurde beendet.`
        };
      case 'HORSE_VET_REMINDER':
        return {
          icon: Carrot,
          title: 'Erinnerung: Tierarzttermin',
          message: `Für "${metadata.horseName}" steht ein Tierarzttermin am ${metadata.details?.appointmentDate?.toLocaleDateString('de-DE')} an.`
        };
      case 'HORSE_FARRIER_REMINDER':
        return {
          icon: Carrot,
          title: 'Erinnerung: Hufschmiedtermin',
          message: `Für "${metadata.horseName}" steht ein Hufschmiedtermin am ${metadata.details?.appointmentDate?.toLocaleDateString('de-DE')} an.`
        };

      // Booking notifications
      case 'BOOKING_CREATED':
        const bookingTime = formatDateTime(metadata.startTime);
        return {
          icon: Calendar,
          title: 'Neue Buchung',
          message: `Neue Buchung für ${metadata.horseName} in ${metadata.facilityName} am ${bookingTime.date} um ${bookingTime.time} Uhr`
        };
      case 'BOOKING_CONFIRMED':
        const confirmedTime = formatDateTime(metadata.startTime);
        return {
          icon: Calendar,
          title: 'Buchung bestätigt',
          message: `Ihre Buchung für ${metadata.horseName} am ${confirmedTime.date} um ${confirmedTime.time} Uhr wurde bestätigt`
        };
      case 'BOOKING_CANCELLED':
        const cancelledTime = formatDateTime(metadata.startTime);
        return {
          icon: Calendar,
          title: 'Buchung storniert',
          message: `Die Buchung für ${metadata.horseName} am ${cancelledTime.date} um ${cancelledTime.time} Uhr wurde storniert`
        };
      case 'BOOKING_REMINDER':
        return {
          icon: Calendar,
          title: 'Erinnerung: Bevorstehende Buchung',
          message: `Morgen ${new Date(metadata.startTime).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr mit ${metadata.horseName} in ${metadata.facilityName}`
        };

      // Facility notifications
      case 'FACILITY_MAINTENANCE':
        return {
          icon: Building2,
          title: 'Anlagenwartung',
          message: metadata.message
        };
      case 'FACILITY_AVAILABLE':
        return {
          icon: Building2,
          title: 'Anlage verfügbar',
          message: `${metadata.facilityName} ist wieder verfügbar`
        };
      case 'FACILITY_BLOCKED':
        return {
          icon: Building2,
          title: 'Neue Sperrzeit',
          message: `${metadata.facilityName}: Neue Sperrzeit eingetragen`
        };

      // System notifications
      case 'SYSTEM_ANNOUNCEMENT':
        return {
          icon: Bell,
          title: metadata.title || 'Systemankündigung',
          message: metadata.message
        };
      case 'FEATURE_UPDATE':
        return {
          icon: Bell,
          title: 'Neue Funktionen',
          message: metadata.message
        };
      case 'MAINTENANCE_MODE':
        return {
          icon: AlertTriangle,
          title: 'Wartungsmodus',
          message: metadata.message
        };

      // Benachrichtigungstyp für pending-confirmation Buchungen
      case 'BOOKING_PENDING_CONFIRMATION':
        const pendingBookingTime = formatDateTime(metadata.startTime);
        return {
          icon: Calendar,
          title: 'Buchung ausstehend',
          message: `Ihre Buchung für "${metadata.horseName}" am ${pendingBookingTime.date} um ${pendingBookingTime.time} Uhr in "${metadata.facilityName}" wartet auf Ihre Bestätigung.`
        };

      default:
        return {
          icon: Info,
          title: 'Neue Benachrichtigung',
          message: 'Sie haben eine neue Benachrichtigung erhalten'
        };
    }
  };

  const content = getNotificationContent(metadata);
  const Icon = content.icon;

  const getPriorityStyles = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    }
  };

  return (
    <div
      className={`
        bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4
        ${!readAt ? 'border-l-4 border-indigo-500 dark:border-indigo-400' : ''}
        hover:shadow-md transition-shadow duration-200
      `}
    >
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <Icon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {content.title}
            </p>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityStyles(priority)}`}>
              {priority}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {content.message}
          </p>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {format(createdAt, 'PPp', { locale: de })}
            </span>
            <div className="flex items-center space-x-4">
              {!readAt && (
                <button
                  onClick={() => onMarkAsRead(id)}
                  className="text-xs text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                >
                  Als gelesen markieren
                </button>
              )}
              <button
                onClick={() => onDelete(id)}
                className="text-xs text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
